import type { Student } from '~/@entities/Student'
import type {
  AttendanceEnterRequest,
  AttendanceFileSearchRequest,
  AttendanceQuitRequest,
  EnterAttendanceData,
  GetAcademyAttendancesData,
  GetAcademyAttendancesParams,
  GetGradeAttendancesData,
  GetLessonClassAttendancesData,
  GetLessonClassAttendancesParams,
  GetStudentAttendancesData,
  GetStudentWithAttendanceGroupData,
  QuitAttendanceData,
  RemoveAttendanceByIdListData,
} from '~/api-spec/__generated__/main'
import { httpClient, type HttpFile, type RequestMeta } from '~/http/client'
import type { WithDefaultedOptionals, WithNullableOptionals } from '~/http/request'

import { adaptAttendances, adaptDateAttendances, adaptStudentAttendanceGroups } from './adapters'
import type { Attendance, DateAttendances, StudentAttendanceGroup } from './schemas'

// 2026-10-14 백엔드 develop(6a0f8e7bb) 의 attendance/controller/AttendanceController.kt·request/ 와
// student/controller/StudentController.kt 로 확인.

// 입실·퇴실 시각은 서버가 nullable 로 받고 null 이면 서버 현재 시각으로 처리한다.
// 학부모 안내 문자 발송 여부는 서버 기본값(true)이 있어 생략할 수 있다.
export type AttendanceEnterBody = WithDefaultedOptionals<
  WithNullableOptionals<AttendanceEnterRequest>,
  'sentForParent'
>
export type AttendanceQuitBody = WithDefaultedOptionals<
  WithNullableOptionals<AttendanceQuitRequest>,
  'sentForParent'
>

/** 기간(yyyy-MM-dd, 양 끝 포함) 출결 조회 조건. deduplicated 를 빼면 서버가 false 로 본다 */
export type AttendanceListParams = GetAcademyAttendancesParams

export type AttendanceSheetParams = WithDefaultedOptionals<
  AttendanceFileSearchRequest,
  'deduplicated'
>

/** 학생들을 입실 처리하고 새 입실 기록을 받는다 */
export const enterAttendance = async (
  body: AttendanceEnterBody,
  meta?: RequestMeta,
): Promise<Attendance[]> => {
  const res = await httpClient().post<EnterAttendanceData>('/attendances/enter', body, { meta })
  return adaptAttendances(res.data) ?? []
}

/** 입실 기록을 퇴실 처리하고 퇴실 시각이 채워진 기록을 받는다 */
export const quitAttendance = async (
  body: AttendanceQuitBody,
  meta?: RequestMeta,
): Promise<Attendance[]> => {
  const res = await httpClient().post<QuitAttendanceData>('/attendances/quit', body, { meta })
  return adaptAttendances(res.data) ?? []
}

export const deleteAttendances = async (
  attendanceIds: Attendance['id'][],
  meta?: RequestMeta,
): Promise<void> => {
  await httpClient().delete<RemoveAttendanceByIdListData>('/attendances', {
    data: attendanceIds,
    meta,
  })
}

/** 학원 학생의 기간 출결을 날짜별로 받는다 */
export const getAttendances = async (
  params: AttendanceListParams,
  meta?: RequestMeta,
): Promise<DateAttendances[]> => {
  const res = await httpClient().get<GetAcademyAttendancesData>('/attendances', { params, meta })
  return adaptDateAttendances(res.data) ?? []
}

export const getGradeAttendances = async (
  schoolType: Student['schoolType'],
  grade: Student['grade'],
  params: AttendanceListParams,
  meta?: RequestMeta,
): Promise<DateAttendances[]> => {
  const res = await httpClient().get<GetGradeAttendancesData>(
    `/attendances/grade/${schoolType}/${grade}`,
    { params, meta },
  )
  return adaptDateAttendances(res.data) ?? []
}

export const getLessonClassAttendances = async (
  lessonClassId: GetLessonClassAttendancesParams['lessonClassId'],
  params: AttendanceListParams,
  meta?: RequestMeta,
): Promise<DateAttendances[]> => {
  const res = await httpClient().get<GetLessonClassAttendancesData>(
    `/attendances/lesson-class/${lessonClassId}`,
    { params, meta },
  )
  return adaptDateAttendances(res.data) ?? []
}

export const getStudentAttendances = async (
  studentId: Student['id'],
  params: AttendanceListParams,
  meta?: RequestMeta,
): Promise<DateAttendances[]> => {
  const res = await httpClient().get<GetStudentAttendancesData>(
    `/attendances/student/${studentId}`,
    { params, meta },
  )
  return adaptDateAttendances(res.data) ?? []
}

/** 선생님 반 학생 전체를 학교급·학년으로 묶어 오늘 출결과 함께 받는다 */
export const getStudentAttendanceGroups = async (
  meta?: RequestMeta,
): Promise<StudentAttendanceGroup[]> => {
  const res = await httpClient().get<GetStudentWithAttendanceGroupData>('/students/attendance', {
    meta,
  })
  return adaptStudentAttendanceGroups(res.data) ?? []
}

/** 기간 출결 엑셀 */
export const getAttendanceSheet = async (
  params: AttendanceSheetParams,
  meta?: RequestMeta,
): Promise<HttpFile> => httpClient().getFile('/attendances/xls', { params, meta })
